import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Slider } from 'antd';
import moment from 'utils/cops-moment';
import { Flex, DatePicker, Button, InfoTooltip } from 'ui';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';

// Smaller fontsize to be persistent with the checkboxes of valuefilter
const RangeInfo = styled(Flex)`
  font-size: 16px;
  margin-left: ${p => p.marginLeft && !p.isColumnFilter ? p.marginLeft : '0'};
  ${p => p.isColumnFilter ? 'display: block' : 'display: inline-block'};

`;

const Actions = styled(Flex)`
  margin-top: 20px;
  > :first-child {
    margin-right: 5px;
  }
`;

const Action = styled(Button)`
  text-transform: uppercase;
`;

const StyledDatePicker = styled(DatePicker)`
  .ant-input {
    font-size: 16px;
    padding: 0 8px;
    height: 28px;
  }
`;

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
  display: inline-block;
  margin-right: 10px;
`;

const Text = styled.span`
  width: ${p => p.isColumnFilter ? '50px' : 'auto'};
  margin-right: 10px;
  font-size: 16px;
  display: inline-block;
`;

const ClearButton = styled.span`
  color: ${p => p.theme.colors.theme};
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
`;

const StyledSlider = styled(Slider)`
  margin-right: 40px !important;
  margin-left: 15px !important;
  ${p => p.isColumnFilter && 'width: 90%;'}

  .ant-slider-track {
    height: 2px;
    background-color: #0098be;
  }

  .ant-slider-rail {
    height: 3px;
  }
`;

const getNumValue = (value, unit) => {
  if (unit === 'date') {
    // `moment(value)` will parse with time zomes in mind -> 23:59:59 flips to the next day
    // To prevent that we'll parse with utc
    return moment.utc(value).valueOf();
  }
  return value;
};

// Min from MW could be in the middle of day -> day could not be selectable -> use start of day
const getMinValue = (value, unit) => {
  if (unit === 'date') {
    return moment.utc(value).startOf('day').valueOf();
  }
  return value;
};

// Same as above for the end date -> end of day needed
const getMaxValue = (value, unit) => {
  if (unit === 'date') {
    return moment.utc(value).endOf('day').valueOf();
  }
  return value;
};

const RangeFilter = ({
  description,
  min,
  max,
  unit,
  activeFilters,
  onChange,
  disabled,
  isColumnFilter,
  allowClear = false,
  needsConfirm = false,
  intl,
  inActive = false,
  info = '',
}) => {
  const [currentVal, setCurrentVal] = useState([]);
  const minValue = getMinValue(min, unit);
  const maxValue = getMaxValue(max, unit);

  useEffect(() => {
    // if no active filter active yet, or it's broken somehow, reset to full range.
    // else if the lowest date is lower than the minvalue, use the minvalue instead.
    // else if the highest date is larger than the maxvalue, use the maxvalue instead.
    // else use the two dates that were set with the filter.
    if (!inActive) {
      if (!activeFilters || !activeFilters.length || activeFilters.length !== 2) {
        setCurrentVal([minValue, maxValue]);
      } else if (getNumValue(activeFilters[0], unit) < minValue) {
        setCurrentVal([minValue, getNumValue(activeFilters[1], unit)]);
      } else if (getNumValue(activeFilters[1], unit) > maxValue) {
        setCurrentVal([getNumValue(activeFilters[0], unit), maxValue]);
      } else {
        setCurrentVal([getNumValue(activeFilters[0], unit), getNumValue(activeFilters[1], unit)]);
      }
    } else {
      setCurrentVal([minValue, maxValue]);
    }
  }, [activeFilters, inActive]);

  useEffect(() => {
    if (!activeFilters || !activeFilters.length) {
      if (currentVal[1] < maxValue) {
        setCurrentVal([currentVal[0], maxValue]);
      }
    }
  }, [max]);

  useEffect(() => {
    if (!activeFilters || !activeFilters.length) {
      if (currentVal[0] > minValue) {
        setCurrentVal([minValue, currentVal[1]]);
      }
    }
  }, [min]);

  const onRangeChanged = (values) => {
    // Check if the values are the same as already in state.
    // We have to do this check, because a prop change (state change) of the range values,
    // will trigger the onChange of the Slider, and cause a double load.
    if (!values.every((v, i) => v === currentVal[i])) {
      setCurrentVal(values);
    }
  };

  const updateRange = (values) => {
    if (unit === 'date') {
      let [startDate, endDate] = values;
      startDate = moment(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]');
      endDate = moment(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]');

      onChange([startDate, endDate]);
    } else {
      onChange(values);
    }
  };

  const onMouseUp = (values) => {
    updateRange(values);
  };

  const formatter = (value) => {
    if (unit === 'date') {
      return moment.utc(value).format('DD/MM/YYYY');
    }
    return value;
  };

  const showClearButton = () => <>
    <ClearButton
      onClick={() => activeFilters?.length ?
        onChange([]) : null}
    >
      {'('}
      {intl.formatMessage({ id: 'worklist.filters.CLEAR' })}
      {')'}
    </ClearButton>
  </>;

  return (
    <>
      {!isColumnFilter &&
        <>
          <Title>
            {description.toUpperCase()}
            <InfoTooltip content={info} />
          </Title>
          {allowClear && showClearButton()}
        </>
      }
      <StyledSlider
        value={currentVal}
        range={true}
        min={minValue}
        max={maxValue}
        onChange={onRangeChanged}
        onAfterChange={!needsConfirm ? onMouseUp : null}
        tipFormatter={!needsConfirm ? formatter : null}
        disabled={disabled || inActive}
        isColumnFilter={isColumnFilter}
      />
      <RangeInfo isColumnFilter={isColumnFilter}>
        <Text isColumnFilter={isColumnFilter}><FormattedMessage id='rangeFilter.FROM' /></Text>
        {unit === 'date' ?
          <StyledDatePicker
            data-test-id='range-filter.from'
            disabledDate={(v) => {
              const currentValue = getNumValue(v, unit);
              return minValue > currentValue || currentValue > currentVal[1];
            }}
            onChange={v => onMouseUp([v, currentVal[1]])}
            value={moment.utc(currentVal[0])}
            disabled={disabled || inActive}
            useBodyAsParent={isColumnFilter}
            popupClassName={isColumnFilter && 'ag-custom-component-popup'}
          />
          :
          <span>{formatter(currentVal[0])}</span>
        }
      </RangeInfo>
      <RangeInfo marginLeft={30} isColumnFilter={isColumnFilter}>
        <Text isColumnFilter={isColumnFilter}><FormattedMessage id='rangeFilter.TO' /></Text>
        {unit === 'date' ?
          <StyledDatePicker
            data-test-id='range-filter.to'
            disabledDate={(v) => {
              const currentValue = getNumValue(v, unit);
              return currentValue > maxValue || currentVal[0] > currentValue;
            }}
            onChange={v => onMouseUp([currentVal[0], v])}
            value={moment.utc(currentVal[1])}
            disabled={disabled || inActive}
            useBodyAsParent={isColumnFilter}
            popupClassName={isColumnFilter && 'ag-custom-component-popup'}
          />
          :
          <span>{formatter(currentVal[1])}</span>
        }
      </RangeInfo>
      {allowClear || needsConfirm ?
        <Actions isColumnFilter={isColumnFilter} aic jcsb gap={5} >
          {allowClear && isColumnFilter ? showClearButton() : null}
          {needsConfirm && <Action
            type='secondary'
            disabled={!currentVal?.length}
            onClick={() => onMouseUp(currentVal)}
          >
            {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
          </Action>
          }
        </Actions> :
        null
      }
    </>
  );
};
export default injectIntl(RangeFilter);

RangeFilter.propTypes = {
  description: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  unit: PropTypes.string,
  disabled: PropTypes.bool,
  activeFilters: PropTypes.array,
  isColumnFilter: PropTypes.bool,
  allowClear: PropTypes.bool,
  needsConfirm: PropTypes.bool,
  intl: PropTypes.object,
  inActive: PropTypes.bool,
  info: PropTypes.string,
};
